import React, { useState, useCallback } from 'react';
import moment from 'moment-timezone';
import { TopikRole, TopikUserRoleType } from '@/Data/Topik/types';
import AddUserModal from './AddUserModal';
import UserInfoModal from './UserInfoModal';
import './RoleTable.scss';

// SVG Components
const ViewDetailIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="32" height="32" rx="8" fill="#E3E3E3"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M18.75 11.75C18.75 13.2688 17.5188 14.5 16 14.5C14.4812 14.5 13.25 13.2688 13.25 11.75C13.25 10.2312 14.4812 9 16 9C17.5188 9 18.75 10.2312 18.75 11.75Z" stroke="#828282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M11.2497 19.7341C11.2374 19.3201 10.8918 18.9944 10.4778 19.0067C10.0637 19.019 9.73804 19.3646 9.75033 19.7786L10.5 19.7564L11.2497 19.7341ZM13.885 22.9504V22.2004C13.8779 22.2004 13.8708 22.2005 13.8637 22.2007L13.885 22.9504ZM18.115 22.9504L18.1363 22.2007C18.1292 22.2005 18.1221 22.2004 18.115 22.2004L18.115 22.9504ZM22.2497 19.7786C22.262 19.3646 21.9363 19.019 21.5222 19.0067C21.1082 18.9944 20.7626 19.3201 20.7503 19.7341L21.5 19.7564L22.2497 19.7786ZM20.7503 19.6336C20.7626 20.0476 21.1082 20.3733 21.5222 20.361C21.9363 20.3488 22.262 20.0032 22.2497 19.5891L21.5 19.6114L20.7503 19.6336ZM18.115 16.4174V17.1674C18.1221 17.1674 18.1292 17.1673 18.1363 17.1671L18.115 16.4174ZM13.885 16.4174L13.8637 17.1671C13.8708 17.1673 13.8779 17.1674 13.885 17.1674L13.885 16.4174ZM9.75033 19.5891C9.73804 20.0032 10.0637 20.3488 10.4778 20.361C10.8918 20.3733 11.2374 20.0476 11.2497 19.6336L10.5 19.6114L9.75033 19.5891ZM10.5 19.7564L9.75033 19.7786C9.8165 22.0085 11.6763 23.7634 13.9063 23.7001L13.885 22.9504L13.8637 22.2007C12.4611 22.2405 11.2913 21.1367 11.2497 19.7341L10.5 19.7564ZM13.885 22.9504V23.7004H18.115V22.9504V22.2004H13.885V22.9504ZM18.115 22.9504L18.0937 23.7001C20.3237 23.7634 22.1835 22.0085 22.2497 19.7786L21.5 19.7564L20.7503 19.7341C20.7087 21.1367 19.5389 22.2405 18.1363 22.2007L18.115 22.9504ZM21.5 19.6114L22.2497 19.5891C22.1835 17.3592 20.3237 15.6044 18.0937 15.6677L18.115 16.4174L18.1363 17.1671C19.5389 17.1272 20.7087 18.231 20.7503 19.6336L21.5 19.6114ZM18.115 16.4174V15.6674H13.885V16.4174V17.1674H18.115V16.4174ZM13.885 16.4174L13.9063 15.6677C11.6763 15.6044 9.8165 17.3592 9.75033 19.5891L10.5 19.6114L11.2497 19.6336C11.2913 18.231 12.4611 17.1272 13.8637 17.1671L13.885 16.4174Z" fill="#828282"/>
    </svg>
);

const AddIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M11 7C11 8.10457 10.1046 9 9 9C7.89543 9 7 8.10457 7 7C7 5.89543 7.89543 5 9 5C10.1046 5 11 5.89543 11 7Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M14 15.5C14 17.433 11.761 19 9 19C6.239 19 4 17.433 4 15.5C4 13.567 6.239 12 9 12C11.761 12 14 13.567 14 15.5Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M18.75 5C18.75 4.58579 18.4142 4.25 18 4.25C17.5858 4.25 17.25 4.58579 17.25 5H18H18.75ZM17.25 7C17.25 7.41421 17.5858 7.75 18 7.75C18.4142 7.75 18.75 7.41421 18.75 7H18H17.25ZM18.75 7C18.75 6.58579 18.4142 6.25 18 6.25C17.5858 6.25 17.25 6.58579 17.25 7H18H18.75ZM17.25 9C17.25 9.41421 17.5858 9.75 18 9.75C18.4142 9.75 18.75 9.41421 18.75 9H18H17.25ZM18 7.75C18.4142 7.75 18.75 7.41421 18.75 7C18.75 6.58579 18.4142 6.25 18 6.25V7V7.75ZM16 6.25C15.5858 6.25 15.25 6.58579 15.25 7C15.25 7.41421 15.5858 7.75 16 7.75V7V6.25ZM18 6.25C17.5858 6.25 17.25 6.58579 17.25 7C17.25 7.41421 17.5858 7.75 18 7.75V7V6.25ZM20 7.75C20.4142 7.75 20.75 7.41421 20.75 7C20.75 6.58579 20.4142 6.25 20 6.25V7V7.75ZM18 5H17.25V7H18H18.75V5H18ZM18 7H17.25V9H18H18.75V7H18ZM18 7V6.25H16V7V7.75H18V7ZM18 7V7.75H20V7V6.25H18V7Z" fill="white"/>
    </svg>
);

const ROLE_LABELS: Record<TopikRole, string> = {
    admin:    '관리자',
    manager:  '매니저',
    lecturer: '강의자',
    student:  '학생',
};

type RoleTableProps = {
    role: TopikRole;
    users: TopikUserRoleType[];
    loading: boolean;
    onAdd: (email: string) => Promise<void>;
    onRemove: (email: string) => Promise<void>;
};

const formatDate = (timestamp: number | null): string => {
    if (!timestamp) { return '-'; }
    return moment(timestamp * 1000).tz('Asia/Seoul').format('YYYY-MM-DD HH:mm');
};

const RoleTable: React.FC<RoleTableProps> = ({ role, users, loading, onAdd, onRemove }) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<TopikUserRoleType | null>(null);

    const roleLabel = ROLE_LABELS[role];

    const closeAddModal = useCallback(() => setIsAddModalOpen(false), []);

    const closeInfoModal = useCallback(() => {
        setIsInfoModalOpen(false);
        setSelectedUser(null);
    }, []);

    const openInfoModal = useCallback((user: TopikUserRoleType) => {
        setSelectedUser(user);
        setIsInfoModalOpen(true);
    }, []);

    const handleRemove = useCallback(async () => {
        if (!selectedUser) { return; }
        await onRemove(selectedUser.email);
    }, [selectedUser, onRemove]);

    return (
        <div className="RoleTable">
            <div className="SectionHeader">
                <h2 className="SectionTitle">{roleLabel} 리스트</h2>
                <button className="AddButton" onClick={() => setIsAddModalOpen(true)}>
                    <AddIcon className="AddIcon" />
                    <span>{roleLabel} 추가하기</span>
                </button>
            </div>

            <table className="Table">
                <thead>
                    <tr>
                        <th>{`${roleLabel} 프로필`}</th>
                        <th>{"이메일"}</th>
                        <th>{"최근 접속일"}</th>
                        <th>{"권한부여일"}</th>
                        <th className="right">{"상세보기"}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && users.length === 0 && (
                        <tr><td colSpan={5} className="Loading">{"로딩 중..."}</td></tr>
                    )}
                    {!loading && users.length === 0 && (
                        <tr><td colSpan={5} className="Empty">{`${roleLabel}이/가 없습니다.`}</td></tr>
                    )}
                    {users.map((user) => (
                        <tr key={user.email}>
                            <td className="ProfileCell">
                                <div className="ProfileGroup">
                                    <div className="ProfileImageSmall"></div>
                                    <span className="UserName">{user.name || user.email.split('@')[0]}</span>
                                </div>
                            </td>
                            <td className="Email">{user.email}</td>
                            <td className="Date">{"-"}</td>
                            <td className="Date">{formatDate(user.granted_at)}</td>
                            <td className="ActionCell">
                                <button className="DetailButton" onClick={() => openInfoModal(user)}>
                                    <ViewDetailIcon />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <AddUserModal
                isOpen={isAddModalOpen}
                roleLabel={roleLabel}
                onClose={closeAddModal}
                onSubmit={onAdd}
            />

            <UserInfoModal
                isOpen={isInfoModalOpen}
                roleLabel={roleLabel}
                user={selectedUser}
                onClose={closeInfoModal}
                onRemove={handleRemove}
            />
        </div>
    );
};

export default RoleTable;
